import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { Features } from '@/utils/Features'
import { useFormatter } from '@/utils/useFormatter'

type Offer = Pick<
  ProductOfferFragment,
  'displayItems' | 'deductible' | 'variant' | 'product' | 'addons' | 'priceIntentData'
>

export const useOfferDetails = (
  offer: Offer,
  maskCivilRegistrationAddress?: boolean,
): Array<{ title: string; value: string }> => {
  const formatter = useFormatter()
  const { t } = useTranslation('cart')

  return useMemo(() => {
    const items = offer.displayItems.map((item) => {
      let displayValue = item.displayValue

      // Mask street and zip code if they are from the members folkbokföringsaddress
      if (
        item.key === 'street' &&
        maskCivilRegistrationAddress &&
        Features.enabled('MASKED_ADDRESS') &&
        offer.priceIntentData.useRegistrationAddress === 'true'
      ) {
        displayValue = formatter.maskedStreetAddress(item.value)
      }

      if (
        item.key === 'zipCode' &&
        maskCivilRegistrationAddress &&
        Features.enabled('MASKED_ADDRESS') &&
        offer.priceIntentData.useRegistrationAddress === 'true'
      ) {
        displayValue = formatter.maskedZipCode(item.value)
      }

      return {
        title: item.displayTitle,
        value: displayValue,
      }
    })

    const tierLevelDisplayName = offer.variant.displayNameTier
    if (tierLevelDisplayName) {
      items.push({ title: t('DATA_TABLE_TIER_LABEL'), value: tierLevelDisplayName })
    }

    const deductibleDisplayName = offer.deductible?.displayName
    if (deductibleDisplayName) {
      items.push({ title: t('DATA_TABLE_DEDUCTIBLE_LABEL'), value: deductibleDisplayName })
    }

    const addons = offer.addons
      .filter((addon) => addon.isSelected)
      .map((addon) => ({
        title: addon.variant.displayName,
        value: addon.coverageText,
      }))
    return [...items, ...addons]
  }, [offer, t, formatter, maskCivilRegistrationAddress])
}
